import { decompress as decompressZstd } from "fzstd";

export type SessionVocabularyMode = "none" | "anki" | "flashcards" | "common_words";

export interface SessionVocabularyCard {
  front: string;
  back: string;
  tags?: string[];
}

export interface ImportedAnkiDeck {
  id: string;
  name: string;
  importedAt: string;
  cards: SessionVocabularyCard[];
}

export interface SessionVocabularyContext {
  mode: SessionVocabularyMode;
  languageCode?: string;
  languageName?: string;
  sourceLabel?: string;
  deckId?: string;
  deckName?: string;
  wordLimit?: CommonWordLimit;
  cards?: SessionVocabularyCard[];
}

export interface SavedFlashcardVocabularySource {
  id?: string;
  card_type?: "generated" | "imported" | null;
  sentence?: string | null;
  translation?: string | null;
  front_text?: string | null;
  back_text?: string | null;
  target_language?: string | null;
  is_favorite?: boolean | null;
  source_deck_name?: string | null;
  source_tags?: string[] | null;
}

export const SESSION_VOCABULARY_STORAGE_KEY = "chicky:anki_decks:v1";
export const SESSION_VOCABULARY_SELECTION_STORAGE_KEY = "chicky:session_vocabulary:v1";
export const SESSION_VOCABULARY_SELECTION_EVENT = "chicky:session-vocabulary-selection-changed";
export const SESSION_VOCABULARY_MAX_CARDS = 80;
export const ANKI_TEXT_EXPORT_MAX_BYTES = 5 * 1024 * 1024;
export const ANKI_PACKAGE_MAX_BYTES = 25 * 1024 * 1024;
export const ANKI_IMPORT_MAX_CARDS = 2000;
export const COMMON_WORD_LIMITS = [100, 500, 1000, 2000, 5000] as const;
export type CommonWordLimit = (typeof COMMON_WORD_LIMITS)[number];
const SESSION_VOCABULARY_MAX_TEXT_CHARS = 180;
const SESSION_VOCABULARY_MAX_SERIALIZED_CHARS = 9000;
const APKG_COLLECTION_FILE_NAMES = ["collection.anki21b", "collection.anki21", "collection.anki2"];
const APKG_MAX_COLLECTION_BYTES = 120 * 1024 * 1024;
const SQLITE_DATABASE_HEADER = "SQLite format 3\0";
const SQLITE_SCHEMA_TABLE_NAMES = new Set(["sqlite_master", "sqlite_schema"]);
const ANKI_SOUND_MARKER_PATTERN = /\[sound:[^\]]+\]/gi;

function cleanText(value: unknown, maxChars = SESSION_VOCABULARY_MAX_TEXT_CHARS): string {
  if (typeof value !== "string") return "";
  return value
    .replace(ANKI_SOUND_MARKER_PATTERN, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxChars);
}

function getTextFieldsIgnoringMedia(fields: string[]): Array<{ text: string; index: number }> {
  return fields
    .map((field, index) => ({ text: cleanText(field), index }))
    .filter((field) => Boolean(field.text));
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
}

function parseTags(value: string): string[] {
  return value
    .split(/[,\s]+/)
    .map((tag) => cleanText(tag, 40))
    .filter(Boolean)
    .slice(0, 12);
}

export function parseAnkiExportText(text: string): SessionVocabularyCard[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"));

  const cards: SessionVocabularyCard[] = [];
  for (const line of lines) {
    const cells = line.includes("\t") ? line.split("\t") : splitCsvLine(line);
    const fields = getTextFieldsIgnoringMedia(cells);
    const front = fields[0]?.text || "";
    const back = fields[1]?.text || "";
    if (!front || !back) continue;
    const tagCells = fields[1] ? cells.slice(fields[1].index + 1).join(" ") : "";
    const tags = tagCells ? parseTags(tagCells) : [];

    cards.push({
      front,
      back,
      ...(tags.length ? { tags } : {}),
    });
  }

  return cards;
}

export function isAnkiPackageFileName(fileName: string): boolean {
  return fileName.trim().toLowerCase().endsWith(".apkg");
}

export function getAnkiImportFileSizeLimit(fileName: string): number {
  return isAnkiPackageFileName(fileName) ? ANKI_PACKAGE_MAX_BYTES : ANKI_TEXT_EXPORT_MAX_BYTES;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${bytes} bytes`;
}

export function validateAnkiImportFile(file: Pick<File, "name" | "size">): string | null {
  const limit = getAnkiImportFileSizeLimit(file.name);
  if (file.size > limit) {
    const fileKind = isAnkiPackageFileName(file.name) ? ".apkg package" : "text export";
    return `That Anki ${fileKind} is too large. The limit is ${formatBytes(limit)}.`;
  }
  return null;
}

export function limitAnkiImportCards(cards: SessionVocabularyCard[]): {
  cards: SessionVocabularyCard[];
  limitedCount: number;
} {
  return {
    cards: cards.slice(0, ANKI_IMPORT_MAX_CARDS),
    limitedCount: Math.max(0, cards.length - ANKI_IMPORT_MAX_CARDS),
  };
}

export async function parseAnkiPackage(
  packageData: ArrayBuffer | Uint8Array
): Promise<SessionVocabularyCard[]> {
  const bytes = packageData instanceof Uint8Array ? packageData : new Uint8Array(packageData);
  const collection = await readAnkiPackageCollection(bytes);
  const notes = parseAnkiSqliteNotes(collection);

  return notes
    .map((note) => {
      const fields = getTextFieldsIgnoringMedia(note.fields);
      const front = fields[0]?.text || "";
      const back = fields[1]?.text || "";
      if (!front || !back) return null;

      const tags = note.tags.length ? note.tags : undefined;
      return {
        front,
        back,
        ...(tags ? { tags } : {}),
      };
    })
    .filter((card): card is SessionVocabularyCard => Boolean(card));
}

export function loadImportedAnkiDecks(): ImportedAnkiDeck[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(SESSION_VOCABULARY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((deck) => normalizeImportedDeck(deck))
      .filter((deck): deck is ImportedAnkiDeck => Boolean(deck));
  } catch {
    return [];
  }
}

export function saveImportedAnkiDecks(decks: ImportedAnkiDeck[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SESSION_VOCABULARY_STORAGE_KEY, JSON.stringify(decks.slice(0, 20)));
}

export function loadSessionVocabularySelection(): SessionVocabularyContext | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(SESSION_VOCABULARY_SELECTION_STORAGE_KEY);
    if (!raw) return null;
    return normalizeSessionVocabularyContext(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveSessionVocabularySelection(
  context: SessionVocabularyContext | null | undefined
): void {
  if (typeof window === "undefined") return;
  const normalized = normalizeSessionVocabularyContext(context);
  if (!normalized || normalized.mode === "none") {
    window.localStorage.removeItem(SESSION_VOCABULARY_SELECTION_STORAGE_KEY);
    window.dispatchEvent(new Event(SESSION_VOCABULARY_SELECTION_EVENT));
    return;
  }
  window.localStorage.setItem(SESSION_VOCABULARY_SELECTION_STORAGE_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new Event(SESSION_VOCABULARY_SELECTION_EVENT));
}

export function makeImportedAnkiDeck(
  name: string,
  cards: SessionVocabularyCard[]
): ImportedAnkiDeck {
  return {
    id: `anki_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    name: cleanText(name, 80) || "Imported Anki deck",
    importedAt: new Date().toISOString(),
    cards: cards.slice(0, 2000),
  };
}

export function buildAnkiSessionVocabularyContext(
  deck: ImportedAnkiDeck
): SessionVocabularyContext {
  return {
    mode: "anki",
    sourceLabel: `Anki deck: ${deck.name}`,
    deckId: deck.id,
    deckName: deck.name,
    cards: deck.cards.slice(0, SESSION_VOCABULARY_MAX_CARDS),
  };
}

export function getSessionVocabularyCardHash(card: SessionVocabularyCard): string {
  const tags =
    card.tags
      ?.map((tag) => cleanText(tag, 40))
      .filter(Boolean)
      .sort() || [];
  return hashCompact(`${cleanText(card.front)}\n${cleanText(card.back)}\n${tags.join(",")}`);
}

export function getSavedFlashcardFront(card: SavedFlashcardVocabularySource): string {
  return cleanText(card.front_text) || cleanText(card.sentence) || "";
}

export function getSavedFlashcardBack(card: SavedFlashcardVocabularySource): string {
  return cleanText(card.back_text) || cleanText(card.translation) || "";
}

export function getSavedFlashcardTags(card: SavedFlashcardVocabularySource): string[] {
  const tags = Array.isArray(card.source_tags)
    ? card.source_tags.map((tag) => cleanText(tag, 40)).filter(Boolean)
    : [];
  if (card.card_type) tags.unshift(card.card_type);
  if (card.is_favorite) tags.unshift("favorite");
  return Array.from(new Set(tags)).slice(0, 12);
}

export function buildFlashcardsSessionVocabularyContext(
  flashcards: SavedFlashcardVocabularySource[],
  options?: {
    deckId?: string;
    deckName?: string;
    languageCode?: string;
    sourceLabel?: string;
  }
): SessionVocabularyContext | null {
  const cards = flashcards
    .map((flashcard) => {
      const front = getSavedFlashcardFront(flashcard);
      const back = getSavedFlashcardBack(flashcard);
      if (!front || !back) return null;
      const tags = getSavedFlashcardTags(flashcard);
      return {
        front,
        back,
        ...(tags.length ? { tags } : {}),
      };
    })
    .filter((card): card is SessionVocabularyCard => Boolean(card))
    .slice(0, SESSION_VOCABULARY_MAX_CARDS);

  if (!cards.length) return null;

  return {
    mode: "flashcards",
    languageCode: options?.languageCode,
    sourceLabel: options?.sourceLabel || "Saved flashcards",
    deckId: options?.deckId,
    deckName: options?.deckName || "Saved flashcards",
    cards,
  };
}

export function buildCommonWordsVocabularyContext(params: {
  languageCode: string;
  languageName?: string;
  wordLimit: CommonWordLimit;
}): SessionVocabularyContext {
  const languageLabel = cleanText(params.languageName, 80) || params.languageCode;
  return {
    mode: "common_words",
    languageCode: params.languageCode,
    languageName: cleanText(params.languageName, 80) || undefined,
    sourceLabel: `${params.wordLimit} most common ${languageLabel} words`,
    wordLimit: params.wordLimit,
  };
}

export function serializeSessionVocabularyContext(
  context: SessionVocabularyContext | null | undefined
): string | null {
  const normalized = normalizeSessionVocabularyContext(context);
  if (!normalized || normalized.mode === "none") return null;

  const serialized = JSON.stringify(normalized);
  if (serialized.length <= SESSION_VOCABULARY_MAX_SERIALIZED_CHARS) {
    return serialized;
  }

  if (
    (normalized.mode === "anki" || normalized.mode === "flashcards") &&
    normalized.cards?.length
  ) {
    for (let cardCount = normalized.cards.length - 1; cardCount > 0; cardCount -= 1) {
      const candidate = JSON.stringify({
        ...normalized,
        cards: normalized.cards.slice(0, cardCount),
      });
      if (candidate.length <= SESSION_VOCABULARY_MAX_SERIALIZED_CHARS) {
        return candidate;
      }
    }
    return null;
  }

  return null;
}

export function getSessionVocabularyCacheKey(
  context: SessionVocabularyContext | null | undefined
): string {
  return hashCompact(serializeSessionVocabularyContext(context) || "");
}

export function normalizeSessionVocabularyContext(
  context: unknown
): SessionVocabularyContext | null {
  if (!context || typeof context !== "object") return null;
  const raw = context as Record<string, unknown>;
  const mode = raw.mode;

  if (mode === "anki" || mode === "flashcards") {
    const cards = Array.isArray(raw.cards)
      ? raw.cards
          .map((card) => normalizeCard(card))
          .filter((card): card is SessionVocabularyCard => Boolean(card))
          .slice(0, SESSION_VOCABULARY_MAX_CARDS)
      : [];
    if (!cards.length) return null;

    return {
      mode,
      sourceLabel:
        cleanText(raw.sourceLabel, 120) ||
        (mode === "flashcards" ? "Saved flashcards" : "Anki deck"),
      deckId: cleanText(raw.deckId, 80) || undefined,
      deckName:
        cleanText(raw.deckName, 100) ||
        (mode === "flashcards" ? "Saved flashcards" : "Imported Anki deck"),
      cards,
    };
  }

  if (mode === "common_words") {
    const wordLimit = COMMON_WORD_LIMITS.includes(raw.wordLimit as CommonWordLimit)
      ? (raw.wordLimit as CommonWordLimit)
      : 500;
    const languageCode = cleanText(raw.languageCode, 30) || undefined;
    const languageName = cleanText(raw.languageName, 80) || undefined;
    const languageLabel = languageName || languageCode || "selected language";
    return {
      mode: "common_words",
      languageCode,
      languageName,
      sourceLabel:
        cleanText(raw.sourceLabel, 120) || `${wordLimit} most common ${languageLabel} words`,
      wordLimit,
    };
  }

  return null;
}

function normalizeImportedDeck(deck: unknown): ImportedAnkiDeck | null {
  if (!deck || typeof deck !== "object") return null;
  const raw = deck as Record<string, unknown>;
  const cards = Array.isArray(raw.cards)
    ? raw.cards.map((card) => normalizeCard(card)).filter(Boolean)
    : [];
  if (!cards.length) return null;

  return {
    id: cleanText(raw.id, 80) || `anki_${Date.now().toString(36)}`,
    name: cleanText(raw.name, 80) || "Imported Anki deck",
    importedAt: cleanText(raw.importedAt, 40) || new Date().toISOString(),
    cards: cards as SessionVocabularyCard[],
  };
}

function normalizeCard(card: unknown): SessionVocabularyCard | null {
  if (!card || typeof card !== "object") return null;
  const raw = card as Record<string, unknown>;
  const front = cleanText(raw.front);
  const back = cleanText(raw.back);
  if (!front || !back) return null;

  return {
    front,
    back,
    ...(Array.isArray(raw.tags)
      ? {
          tags: raw.tags
            .map((tag) => cleanText(tag, 40))
            .filter(Boolean)
            .slice(0, 12),
        }
      : {}),
  };
}

export function hashCompact(value: string): string {
  if (!value) return "";
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return `${value.length}:${(hash >>> 0).toString(36)}`;
}

interface AnkiSqliteNote {
  fields: string[];
  tags: string[];
}

interface ZipEntry {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

async function readAnkiPackageCollection(bytes: Uint8Array): Promise<Uint8Array> {
  const entries = readZipCentralDirectory(bytes);
  const collectionEntry = APKG_COLLECTION_FILE_NAMES.map((name) =>
    entries.find((entry) => entry.name === name)
  ).find((entry): entry is ZipEntry => Boolean(entry));

  if (!collectionEntry) {
    throw new Error("This .apkg file does not contain an Anki collection database.");
  }

  if (collectionEntry.uncompressedSize > APKG_MAX_COLLECTION_BYTES) {
    throw new Error("This Anki package is too large to import in the browser.");
  }

  const collectionData = await readZipEntryData(bytes, collectionEntry);
  if (collectionEntry.name === "collection.anki21b") {
    return readAnki21bCollectionData(collectionData);
  }

  return collectionData;
}

function readAnki21bCollectionData(collectionData: Uint8Array): Uint8Array {
  if (
    decodeAscii(collectionData.subarray(0, SQLITE_DATABASE_HEADER.length)) ===
    SQLITE_DATABASE_HEADER
  ) {
    return collectionData;
  }

  try {
    return decompressZstd(collectionData);
  } catch {
    throw new Error(
      "This newer Anki package uses a collection format that could not be decompressed."
    );
  }
}

function parseAnkiSqliteNotes(database: Uint8Array): AnkiSqliteNote[] {
  const header = decodeAscii(database.subarray(0, SQLITE_DATABASE_HEADER.length));
  if (header !== SQLITE_DATABASE_HEADER) {
    throw new Error("This .apkg package contains an unreadable Anki database.");
  }

  const pageSizeRaw = readUint16(database, 16);
  const pageSize = pageSizeRaw === 1 ? 65536 : pageSizeRaw;
  if (pageSize < 512 || pageSize > 65536) {
    throw new Error("This .apkg package uses an unsupported Anki database page size.");
  }
  const usablePageSize = pageSize - (database[20] || 0);

  const schemaRows = readSqliteTableRows(database, pageSize, usablePageSize, 1);
  const notesSchema = schemaRows.find((row) => {
    const type = sqliteValueToString(row[0]);
    const name = sqliteValueToString(row[1]);
    return type === "table" && name === "notes";
  });
  const notesRootPage = sqliteValueToNumber(notesSchema?.[3]);
  if (!notesRootPage) {
    throw new Error("This .apkg package does not contain an Anki notes table.");
  }

  const notesRows = readSqliteTableRows(database, pageSize, usablePageSize, notesRootPage);
  return notesRows
    .map((row) => {
      const tagsText = sqliteValueToString(row[5]);
      const fieldsText = sqliteValueToString(row[6]);
      if (!fieldsText) return null;

      return {
        fields: fieldsText.split("\x1f"),
        tags: parseTags(tagsText),
      };
    })
    .filter((note): note is AnkiSqliteNote => Boolean(note));
}

function readSqliteTableRows(
  database: Uint8Array,
  pageSize: number,
  usablePageSize: number,
  rootPage: number
): unknown[][] {
  const rows: unknown[][] = [];
  const seenPages = new Set<number>();

  const visitPage = (pageNumber: number) => {
    if (!Number.isInteger(pageNumber) || pageNumber < 1 || seenPages.has(pageNumber)) return;
    seenPages.add(pageNumber);

    const pageStart = (pageNumber - 1) * pageSize;
    if (pageStart < 0 || pageStart >= database.length) return;

    const btreeOffset = pageNumber === 1 ? pageStart + 100 : pageStart;
    const pageType = database[btreeOffset];
    const cellCount = readUint16(database, btreeOffset + 3);
    const cellPointerStart = btreeOffset + (pageType === 0x05 ? 12 : 8);

    if (pageType === 0x0d) {
      for (let index = 0; index < cellCount; index += 1) {
        const cellOffset = pageStart + readUint16(database, cellPointerStart + index * 2);
        const row = readSqliteTableLeafCell(database, pageSize, usablePageSize, cellOffset);
        if (row) rows.push(row);
      }
      return;
    }

    if (pageType === 0x05) {
      for (let index = 0; index < cellCount; index += 1) {
        const cellOffset = pageStart + readUint16(database, cellPointerStart + index * 2);
        visitPage(readUint32(database, cellOffset));
      }
      visitPage(readUint32(database, btreeOffset + 8));
    }
  };

  visitPage(rootPage);
  return rows;
}

function readSqliteTableLeafCell(
  database: Uint8Array,
  pageSize: number,
  usablePageSize: number,
  cellOffset: number
): unknown[] | null {
  const payloadSize = readSqliteVarint(database, cellOffset);
  const rowId = readSqliteVarint(database, payloadSize.nextOffset);
  const payloadOffset = rowId.nextOffset;
  const payloadLength = payloadSize.value;
  if (payloadLength <= 0) return null;

  const localPayloadLength = getSqliteLocalPayloadLength(payloadLength, usablePageSize);
  const localPayloadEnd = payloadOffset + localPayloadLength;
  if (localPayloadEnd > database.length) return null;

  if (localPayloadLength >= payloadLength) {
    return readSqliteRecord(database.subarray(payloadOffset, localPayloadEnd));
  }

  const payload = new Uint8Array(payloadLength);
  payload.set(database.subarray(payloadOffset, localPayloadEnd), 0);
  let written = localPayloadLength;
  if (localPayloadEnd + 4 > database.length) return null;
  let overflowPageNumber = readUint32(database, localPayloadEnd);

  while (overflowPageNumber && written < payloadLength) {
    const overflowStart = (overflowPageNumber - 1) * pageSize;
    if (overflowStart < 0 || overflowStart + 4 > database.length) return null;

    const nextOverflowPageNumber = readUint32(database, overflowStart);
    const chunk = database.subarray(
      overflowStart + 4,
      Math.min(overflowStart + usablePageSize, database.length)
    );
    const remaining = payloadLength - written;
    payload.set(chunk.subarray(0, remaining), written);
    written += Math.min(chunk.length, remaining);
    overflowPageNumber = nextOverflowPageNumber;
  }

  if (written < payloadLength) return null;
  return readSqliteRecord(payload);
}

function getSqliteLocalPayloadLength(payloadLength: number, usablePageSize: number): number {
  const maxLocal = usablePageSize - 35;
  if (payloadLength <= maxLocal) return payloadLength;

  const minLocal = Math.floor(((usablePageSize - 12) * 32) / 255) - 23;
  const candidate = minLocal + ((payloadLength - minLocal) % (usablePageSize - 4));
  return candidate <= maxLocal ? candidate : minLocal;
}

function readSqliteRecord(payload: Uint8Array): unknown[] {
  const headerSizeVarint = readSqliteVarint(payload, 0);
  const headerSize = headerSizeVarint.value;
  const serialTypes: number[] = [];
  let offset = headerSizeVarint.nextOffset;

  while (offset < headerSize) {
    const serialType = readSqliteVarint(payload, offset);
    serialTypes.push(serialType.value);
    offset = serialType.nextOffset;
  }

  const values: unknown[] = [];
  let bodyOffset = headerSize;
  for (const serialType of serialTypes) {
    const decoded = decodeSqliteValue(payload, bodyOffset, serialType);
    values.push(decoded.value);
    bodyOffset = decoded.nextOffset;
  }

  return values;
}

function decodeSqliteValue(
  payload: Uint8Array,
  offset: number,
  serialType: number
): { value: unknown; nextOffset: number } {
  const type = serialType;
  if (type === 0) return { value: null, nextOffset: offset };
  if (type === 1) return { value: readSignedInteger(payload, offset, 1), nextOffset: offset + 1 };
  if (type === 2) return { value: readSignedInteger(payload, offset, 2), nextOffset: offset + 2 };
  if (type === 3) return { value: readSignedInteger(payload, offset, 3), nextOffset: offset + 3 };
  if (type === 4) return { value: readSignedInteger(payload, offset, 4), nextOffset: offset + 4 };
  if (type === 5) return { value: readSignedInteger(payload, offset, 6), nextOffset: offset + 6 };
  if (type === 6) return { value: readSignedInteger(payload, offset, 8), nextOffset: offset + 8 };
  if (type === 7) {
    const view = new DataView(payload.buffer, payload.byteOffset + offset, 8);
    return { value: view.getFloat64(0, false), nextOffset: offset + 8 };
  }
  if (type === 8) return { value: 0, nextOffset: offset };
  if (type === 9) return { value: 1, nextOffset: offset };

  if (type >= 12) {
    const length = Math.floor((type - 12) / 2);
    const nextOffset = offset + length;
    const raw = payload.subarray(offset, nextOffset);
    if (type % 2 === 0) return { value: raw, nextOffset };
    return { value: new TextDecoder().decode(raw), nextOffset };
  }

  return { value: null, nextOffset: offset };
}

function readSqliteVarint(
  bytes: Uint8Array,
  offset: number
): { value: number; nextOffset: number } {
  let value = 0;

  for (let index = 0; index < 9; index += 1) {
    const byte = bytes[offset + index];
    if (byte === undefined) return { value, nextOffset: offset + index };

    if (index === 8) {
      value = value * 256 + byte;
      return { value, nextOffset: offset + 9 };
    }

    value = value * 128 + (byte & 0x7f);
    if ((byte & 0x80) === 0) {
      return { value, nextOffset: offset + index + 1 };
    }
  }

  return { value, nextOffset: offset + 9 };
}

function readZipCentralDirectory(bytes: Uint8Array): ZipEntry[] {
  const eocdOffset = findZipEndOfCentralDirectory(bytes);
  if (eocdOffset < 0) {
    throw new Error("This .apkg file is not a readable ZIP package.");
  }

  const entryCount = readUint16(bytes, eocdOffset + 10, true);
  const centralDirectoryOffset = readUint32(bytes, eocdOffset + 16, true);
  const entries: ZipEntry[] = [];
  let offset = centralDirectoryOffset;

  for (let index = 0; index < entryCount; index += 1) {
    if (readUint32(bytes, offset, true) !== 0x02014b50) break;
    const compressionMethod = readUint16(bytes, offset + 10, true);
    const compressedSize = readUint32(bytes, offset + 20, true);
    const uncompressedSize = readUint32(bytes, offset + 24, true);
    const fileNameLength = readUint16(bytes, offset + 28, true);
    const extraLength = readUint16(bytes, offset + 30, true);
    const commentLength = readUint16(bytes, offset + 32, true);
    const localHeaderOffset = readUint32(bytes, offset + 42, true);
    const name = new TextDecoder().decode(
      bytes.subarray(offset + 46, offset + 46 + fileNameLength)
    );

    entries.push({
      name,
      compressionMethod,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });
    offset += 46 + fileNameLength + extraLength + commentLength;
  }

  return entries;
}

async function readZipEntryData(bytes: Uint8Array, entry: ZipEntry): Promise<Uint8Array> {
  const localOffset = entry.localHeaderOffset;
  if (readUint32(bytes, localOffset, true) !== 0x04034b50) {
    throw new Error("This .apkg file has a corrupt Anki collection entry.");
  }

  const fileNameLength = readUint16(bytes, localOffset + 26, true);
  const extraLength = readUint16(bytes, localOffset + 28, true);
  const dataOffset = localOffset + 30 + fileNameLength + extraLength;
  const compressedData = bytes.subarray(dataOffset, dataOffset + entry.compressedSize);

  if (entry.compressionMethod === 0) return compressedData;
  if (entry.compressionMethod === 8) return inflateRawZipData(compressedData);

  throw new Error("This .apkg package uses an unsupported ZIP compression method.");
}

async function inflateRawZipData(compressedData: Uint8Array): Promise<Uint8Array> {
  const streamCtor = globalThis.DecompressionStream;
  if (typeof streamCtor === "function") {
    const exactBuffer = new ArrayBuffer(compressedData.byteLength);
    new Uint8Array(exactBuffer).set(compressedData);
    const stream = new Blob([exactBuffer]).stream().pipeThrough(new streamCtor("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  const maybeRequire = eval("typeof require === 'function' ? require : undefined") as
    | undefined
    | ((specifier: string) => { inflateRawSync(data: Uint8Array): Uint8Array });
  if (maybeRequire) {
    const zlib = maybeRequire("node:zlib");
    return new Uint8Array(zlib.inflateRawSync(compressedData));
  }

  throw new Error("This browser cannot decompress this Anki package.");
}

function findZipEndOfCentralDirectory(bytes: Uint8Array): number {
  const minOffset = Math.max(0, bytes.length - 0xffff - 22);
  for (let offset = bytes.length - 22; offset >= minOffset; offset -= 1) {
    if (readUint32(bytes, offset, true) === 0x06054b50) return offset;
  }
  return -1;
}

function sqliteValueToString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function sqliteValueToNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function readUint16(bytes: Uint8Array, offset: number, littleEndian = false): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 2);
  return view.getUint16(0, littleEndian);
}

function readUint32(bytes: Uint8Array, offset: number, littleEndian = false): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 4);
  return view.getUint32(0, littleEndian);
}

function readSignedInteger(bytes: Uint8Array, offset: number, length: number): number {
  let value = 0;
  for (let index = 0; index < length; index += 1) {
    value = value * 256 + (bytes[offset + index] || 0);
  }

  const signBit = 2 ** (length * 8 - 1);
  if (value >= signBit) value -= 2 ** (length * 8);
  return value;
}

function decodeAscii(bytes: Uint8Array): string {
  return String.fromCharCode(...bytes);
}
